import React, { useCallback, useContext, useMemo, useState } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Pane from 'components/Common/Pane'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import GeoContext from 'contexts/geoContext'
import { getHomeOfferListParams } from 'home/helpers'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import config from 'constants/config'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { encodeSearchParams } from 'lib/search/searchUtils'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import useCurrentSiteTakeover from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import BodyText from 'LuxKit/Typography/BodyText'
import SiteTakeoverBackgroundImage from 'takeovers/utils/components/SiteTakeoverBackgroundImage'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import FilterChip from 'LuxKit/Chips/FilterChip'
import Carousel from 'LuxKit/Carousel/Carousel'
import * as Analytics from 'analytics/analytics'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { isHeroBannerPayload } from 'lib/lab/dynamicPayload'
import Image from 'components/Common/Image'
import { GenericBackgroundImage } from 'takeovers/utils/components/GenericBackgroundImage'
import TextLink from 'LuxKit/TextLink'
import Sheet from 'components/utils/Sheet'

const SiteTakeoverBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
`

const CarouselContainer = styled(Pane)`
  position: relative;
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
  ${mediaQueryUp.desktop} {
    /**
     * This adds room for the search section on all homepages
     */
    padding-bottom: ${rem(100)};

  }
`

const Content = styled(VerticalSpacer)`
  position: relative;
  z-index: 1;
`

export interface HeroOfferCarouselFilterOption {
  label: string;
  value: string;
  type: 'holidayType' | 'campaignTag';
}

const defaultFilterOptions: Array<HeroOfferCarouselFilterOption> = [
  { label: 'Bonus Dining & Drinks', value: 'Dining & Drinks', type: 'holidayType' },
  { label: 'Family Friendly', value: 'Family Friendly', type: 'holidayType' },
  { label: 'Sun & Beach', value: 'Sun & Beach', type: 'holidayType' },
]

interface HeroOfferCarouselTakeoverPayload {
  carouselPalette?: App.Design.PaletteType;
  title?: React.ReactNode | string;
  subtitle?: React.ReactNode | string;
  subtitleActionLink?: string;
  logo?: React.ReactNode;
  background?: React.ReactNode;
  filterOptions: Array<HeroOfferCarouselFilterOption>;
}

function transformTakeoverPayload(takeoverPayload: App.SiteTakeoverConfig['heroSection'] | App.LabHeroCarouselPayload): HeroOfferCarouselTakeoverPayload {
  if (isHeroBannerPayload(takeoverPayload)) {
    return {
      carouselPalette: takeoverPayload.palette,
      title: takeoverPayload.title,
      subtitle: takeoverPayload.subtitle,
      subtitleActionLink: takeoverPayload.subtitleActionLink,
      logo: takeoverPayload.logoRef ? <Image image={{ id: takeoverPayload.logoRef }} dpr={1} width={80} height={80} /> : undefined,
      background: takeoverPayload.backgroundRef ? <GenericBackgroundImage imageId={takeoverPayload.backgroundRef} backgroundStyle={takeoverPayload.backgroundStyle} /> : undefined,
      filterOptions: takeoverPayload.filters ?? defaultFilterOptions,
    }
  }

  return {
    carouselPalette: takeoverPayload.palette,
    title: takeoverPayload.title,
    subtitle: takeoverPayload.subtitle,
    subtitleActionLink: takeoverPayload.subtitleActionLink,
    logo: takeoverPayload.logo,
    background: <SiteTakeoverBackgroundImage background="hero" />,
    filterOptions: takeoverPayload.filters ?? defaultFilterOptions,
  }
}

interface Props {
  className?: string;
  overrideTitle?: React.ReactNode;
  overrideSubtitle?: React.ReactNode;
  overrideOfferList?: App.OfferList;
}

function HeroOfferCarouselSection(props: Props) {
  const { className, overrideTitle, overrideSubtitle, overrideOfferList } = props
  const { currentRegionCode, geoStateName } = useContext(GeoContext)
  const [selectedFilter, setSelectedFilter] = useState<HeroOfferCarouselFilterOption | undefined>()
  const overrideOfferListFetching = !!overrideOfferList?.fetching

  // Current site takeover implementation
  const siteTakeover = useCurrentSiteTakeover({ heroVertical: 'home' })

  // Hero carousel takeover payload from svc-lab
  const enableHeroCarouselTakeover = useLabFeatureFlag(LabFeatureFlags.enableHeroCarouselTakeover)
  const heroCarouselTakeoverPayload: App.LabHeroCarouselPayload | undefined = useMemo(() => {
    if (enableHeroCarouselTakeover?.enabled) {
      const payload = enableHeroCarouselTakeover.value
      if (isHeroBannerPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableHeroCarouselTakeover])

  const heroCarouselConfig = useMemo(() => {
    if (heroCarouselTakeoverPayload) {
      return transformTakeoverPayload(heroCarouselTakeoverPayload)
    } else if (siteTakeover?.heroSection) {
      return transformTakeoverPayload(siteTakeover.heroSection)
    }
    return undefined
  }, [heroCarouselTakeoverPayload, siteTakeover])

  const filterOptions = heroCarouselConfig?.filterOptions ?? defaultFilterOptions

  const toggleFilter = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const filter = e.currentTarget.dataset.value!
    const nextFilterValue = selectedFilter?.value === filter ? undefined : filter
    const nextFilter = nextFilterValue ? filterOptions.find(option => option.value === nextFilterValue) : undefined

    if (nextFilterValue) {
      Analytics.trackClientEvent({
        subject: 'click_button',
        action: filter,
        type: 'interaction',
        category: 'homepage',
      })
    }
    setSelectedFilter(nextFilter)
  }, [selectedFilter, filterOptions])

  const filters = useMemo<App.OfferListFilters>((): App.OfferListFilters => {
    return {
      ...getHomeOfferListParams({
        region: currentRegionCode,
        subregion: geoStateName,
      }),
      holidayTypes: selectedFilter?.type === 'holidayType' ? [selectedFilter.value] : undefined,
      campaigns: selectedFilter?.type === 'campaignTag' ? [selectedFilter.value] : undefined,
    }
  }, [currentRegionCode, geoStateName, selectedFilter])

  const searchUrl = useMemo(() => {
    const queryParams = {
      searchItem: ANYWHERE_SEARCH_ITEM,
      rooms: [config.search.defaultOccupants],
    }

    const query = encodeSearchParams(queryParams)
    return `/search?${query.toString()}`
  }, [])

  const carouselPalette = heroCarouselConfig?.carouselPalette ?? 'inverse'

  const title = overrideTitle ?? heroCarouselConfig?.title ??
    (isAgentHubEnabled ? <>Today's <i>featured</i> escapes</> : <>Today's top <i>exclusive</i> offers</>)

  const subtitle = overrideSubtitle ?? heroCarouselConfig?.subtitle
  const subtitleActionLink = heroCarouselConfig?.subtitleActionLink

  const takeOverLogo = heroCarouselConfig?.logo

  const takeOverBackground = heroCarouselConfig?.background

  const showFilters = !overrideOfferList && !!filterOptions.length

  return (
    <CarouselContainer paletteType={carouselPalette} className={className} data-testid="hero-carousel">
      {takeOverBackground && <SiteTakeoverBackground>
        {takeOverBackground}
      </SiteTakeoverBackground>}
      <Content gap={24}>
        <LayoutContainer>
          <VerticalSpacer gap={16}>
            <Group
              direction="horizontal"
              horizontalAlign="space-between"
              verticalAlign="center"
              gap={16}
            >
              <Group direction="horizontal" gap={16} verticalAlign="center">
                {takeOverLogo}
                <VerticalSpacer gap={2}>
                  {overrideOfferListFetching ? <TextLoadingBox typography="heading2" width="15ch" /> : <Heading variant="heading2">{title}</Heading>}
                  {!!subtitle && <Group direction="horizontal" gap={8} verticalAlign="center">
                    <BodyText variant="large">
                      {subtitle}
                      {' '}
                      {subtitleActionLink && <TextLink wrap="no-wrap" to={subtitleActionLink}>View all offers</TextLink>}
                    </BodyText>
                  </Group>}
                </VerticalSpacer>
              </Group>
              <CSSBreakpoint min="tablet">
                <Group direction="horizontal" gap={16} verticalAlign="center">
                  {showFilters && !overrideOfferListFetching && <CSSBreakpoint min="desktop">
                    <Sheet paletteType="default">
                      <Group direction="horizontal" gap={8}>
                        {filterOptions.map(option =>
                          <FilterChip
                            key={option.value}
                            size="medium"
                            selected={option.value === selectedFilter?.value}
                            data-value={option.value}
                            onClick={toggleFilter}
                          >
                            {option.label}
                          </FilterChip>,
                        )}
                      </Group>
                    </Sheet>
                  </CSSBreakpoint>}
                  {!heroCarouselConfig && <TextButton
                    kind="tertiary"
                    to={searchUrl}
                  >
                    View all
                  </TextButton>}
                </Group>
              </CSSBreakpoint>
            </Group>
            {showFilters && !overrideOfferListFetching && <CSSBreakpoint max="tablet">
              <Sheet paletteType="default">
                <Carousel gap={8} snap="start">
                  {filterOptions.map(option =>
                    <FilterChip
                      key={option.value}
                      size="medium"
                      selected={option.value === selectedFilter?.value}
                      data-value={option.value}
                      onClick={toggleFilter}
                    >
                      {option.label}
                    </FilterChip>,
                  )}
                </Carousel>
              </Sheet>
            </CSSBreakpoint>}
          </VerticalSpacer>
        </LayoutContainer>
        <Sheet paletteType={siteTakeover?.heroSection.carouselPalette}>
          <OfferListCarousel
            overrideOfferList={overrideOfferList}
            filters={filters}
            tileStyle={siteTakeover?.heroSection.carouselPalette ? 'card-opaque' : 'card-default'}
            tileSize="medium"
            eagerLoadImages
          >
            <OfferCarouselLookingForSomethingElse to={searchUrl} />
          </OfferListCarousel>
        </Sheet>
        {!heroCarouselConfig && <CSSBreakpoint max="mobile">
          <TextButton
            kind="tertiary"
            to={searchUrl}
          >
            View all
          </TextButton>
        </CSSBreakpoint>}
      </Content>
    </CarouselContainer>
  )
}

export default React.memo(HeroOfferCarouselSection)
